import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  useTheme,
  type Theme,
} from "@mui/material";
import { metricLabel } from "../../utils/metricOptions";
import { useReportStats } from "../../hooks/useReportData";
import type { KpiCardsBlock } from "../../api/types";

interface KpiCardsViewProps {
  block: KpiCardsBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

const ragTextColor = (theme: Theme, ragColor?: string): string => {
  switch (ragColor) {
    case "green":
      return theme.palette.success.main;
    case "amber":
      return theme.palette.warning.main;
    case "red":
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

/**
 * Row of KPI stat cards: big value + unit, metric label, and the change string
 * coloured by the metric's RAG colour. Responsive grid.
 */
export const KpiCardsView: React.FC<KpiCardsViewProps> = ({
  block,
  clientId,
  branding,
}) => {
  const theme = useTheme();
  const accent = branding?.primaryColor ?? theme.palette.primary.main;
  const query = useReportStats(block.metrics, block.filters, clientId);

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load KPI data.</Alert>;
  }

  const stats = query.data?.data ?? {};
  const columns = block.metrics.filter((m) => stats[m]);
  if (columns.length === 0) {
    return <Alert severity="info">No KPI data for this period.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      <Grid container spacing={2}>
        {columns.map((m) => {
          const stat = stats[m];
          const hasValue = stat.value !== null;
          const value = hasValue
            ? Math.round((stat.value as number) * 10) / 10
            : "No data";
          return (
            <Grid key={m} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                variant="outlined"
                sx={{ height: "100%", borderTop: `3px solid ${accent}` }}
              >
                <CardContent>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 600, mb: 1 }}
                  >
                    {stat.label ?? metricLabel(m)}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 800, lineHeight: 1, color: accent }}
                  >
                    {value}
                    {hasValue && stat.unit && (
                      <Typography
                        component="span"
                        variant="h6"
                        sx={{ ml: 0.5, color: "text.secondary", fontWeight: 600 }}
                      >
                        {stat.unit}
                      </Typography>
                    )}
                  </Typography>
                  {stat.change && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        color: ragTextColor(theme, stat.rag_color),
                      }}
                    >
                      {stat.change}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
